import { createSelector } from "@reduxjs/toolkit"
import { selectProvider } from "../Provider/model"
import {
  getOnboardingFlowIdFromState,
  isUserOnboarded,
  autoSelectAffiliatedOrInvitePracticeId,
} from "./helpers"
import {
  selectUser,
  selectValidatedHealthAdvocateUser,
} from "../../features/shared/selectors"
import { PartialPractice } from "./schema"
import {
  PracticeInviteWithContext,
  PracticeStatusEnum,
} from "../Practice/schema"
import { PracticeRoleEnum, selectAllPracticeRoles } from "../Account/userSlice"
import { RootState } from "../../reduxStore/store"
import { PracticesDataStatus } from "./model"
import { Provider } from "../Provider/schema"
import { hasConditionalReqs } from "./utils"

export const selectPractices = (state: RootState): PartialPractice[] => {
  return state.practices.practices
}

export const selectDetachedProviders = (state: RootState): Provider[] => {
  return state.practices.detachedProviders
}

export const selectPracticeInvites = (
  state: RootState,
): PracticeInviteWithContext[] => {
  return state.practices.practiceInvites
}

export const selectLinkedInviteId = (state: RootState): string | undefined => {
  return state.practices.linkedInviteId
}

export const selectPracticesDataStatuses = (
  state: RootState,
): PracticesDataStatus => {
  return state.practices.dataStatuses
}

export const selectAutoSelectedPracticeResult = createSelector(
  [
    selectPractices,
    selectPracticeInvites,
    selectValidatedHealthAdvocateUser,
    selectLinkedInviteId,
  ],
  (partialPractices, practiceInvites, isHAUser, linkedInviteId) => {
    if (isHAUser) {
      return undefined
    }
    return autoSelectAffiliatedOrInvitePracticeId(
      partialPractices,
      practiceInvites,
      linkedInviteId,
    )
  },
)

export const selectAutoSelectedPracticeHasConditionalRequirements =
  createSelector(
    [selectPractices, selectAutoSelectedPracticeResult],
    (partialPractices, autoSelectedPracticeIdResult) => {
      const practice = partialPractices.find(
        (practice) => practice.id === autoSelectedPracticeIdResult?.practiceId,
      )
      return hasConditionalReqs(practice)
    },
  )

export const selectOnboardingFlowId = createSelector(
  [
    selectPractices,
    selectProvider,
    selectDetachedProviders,
    selectPracticeInvites,
    selectAutoSelectedPracticeResult,
  ],
  (
    partialPractices,
    provider,
    detachedProviders,
    practiceInvites,
    autoSelectedPracticeIdResult,
  ) =>
    (isSeeingPatients = false) =>
      getOnboardingFlowIdFromState({
        partialPractices,
        provider,
        detachedProviders,
        practiceInvites,
        autoSelectedPracticeIdResult,
        isSeeingPatients,
      }),
)

export const selectIsUserOnboarded = createSelector(
  [
    selectOnboardingFlowId,
    selectUser,
    selectPractices,
    selectProvider,
    selectAutoSelectedPracticeResult,
    selectValidatedHealthAdvocateUser,
  ],
  (
    getOnboardingFlowId,
    user,
    practices,
    provider,
    autoSelectedPracticeResult,
    isHAUser,
  ) => {
    const flowId = getOnboardingFlowId()
    const completedUserOnboardingSteps = user.completedUserOnboardingSteps ?? []
    const affiliatedPractice = practices.find(
      (practice: PartialPractice) =>
        practice.id === autoSelectedPracticeResult?.practiceId,
    )

    return isUserOnboarded(
      flowId,
      completedUserOnboardingSteps,
      affiliatedPractice,
      provider,
      isHAUser,
    )
  },
)

export const selectHasPracticeCreationInProgress = createSelector(
  [selectPractices, selectAllPracticeRoles],
  (practices, practiceRoles) =>
    practices.some(
      (practice) =>
        practice.status === PracticeStatusEnum.PENDING &&
        practiceRoles.some(
          (practiceRoleSet) =>
            practiceRoleSet.practiceId === practice.id &&
            practiceRoleSet.roles.includes(PracticeRoleEnum.PRACTICE_MANAGER) &&
            practiceRoleSet.roles.includes(PracticeRoleEnum.PROVIDER),
        ),
    ),
)
